/**
 * Guided-tour registry (tour spec §4, §6).
 *
 * Selector strategy: every anchor is a `data-tour` attribute on a stable
 * wrapper element — never a Tailwind class or DOM path (spec §3). A repo grep
 * for `data-tour` enumerates every anchor; TourProvider warns in dev when a
 * registered selector is missing on its route after the anchor-poll cap.
 *
 * Four tours + three single coach marks:
 *   A "around"    — "Show me around": a 13-step CROSS-PAGE walkthrough in five
 *                   chapters (Your day → Research → Drafting → Your cases →
 *                   Always available). Steps carry a `route`; the cross-route
 *                   engine in useTour.ts navigates, waits for the anchor
 *                   (~4s cap) and silently skips steps whose anchor never
 *                   appears. Pull-only (checklist link / user-chip menu /
 *                   Settings Help card).
 *   B "memo"      — auto, first visit to a completed /app/research/:id.
 *   C "casefile"  — auto, first visit to /app/cases/:id.
 *   D "drafting"  — auto, first visit to /app/drafts/new.
 *   Coach marks   — assistant composer, eCourts provenance chip, Cmd+K
 *                   (single instances, never chained — spec §4).
 */

import type { Side, Alignment } from 'driver.js';

export type TourId =
  | 'around'
  | 'memo'
  | 'casefile'
  | 'drafting'
  | 'coach-assistant'
  | 'coach-provenance'
  | 'coach-cmdk';

export interface TourStepDef {
  /** strings.ts key ("around.1", "coach.assistant", …). */
  key: string;
  /**
   * Candidate selectors — the first VISIBLE match wins. Lets one step target
   * the desktop sidebar anchor and its mobile bottom-nav twin.
   */
  targets: string[];
  side: Side;
  align?: Alignment;
  /** Position override below the lg breakpoint (spec table "desktop / mobile"). */
  mobileSide?: Side;
  /**
   * Route hosting this step's anchor. Presence of `route` on ANY step makes
   * the tour cross-route: advancing to a step whose route differs from the
   * current pathname navigates first, then waits for the anchor.
   */
  route?: string;
  /** Chapter id (strings.ts `chapters` map) — shown in the progress line. */
  chapter?: string;
}

export interface TourDef {
  id: TourId;
  kind: 'tour' | 'coach';
  /** English display name for the Settings Help card. */
  label: string;
  /** Auto-fires once on first route match (mini-tours + coach marks). */
  auto: boolean;
  routeMatch: (pathname: string) => boolean;
  /**
   * Fixed path for manual launch/replay; null = needs an instance page
   * (the Settings Help card picks a concrete memo/case id from the stores).
   */
  launchPath: string | null;
  steps: TourStepDef[];
  /** Coach-mark gate: only fire from this stored session count onward. */
  minSessions?: number;
  /** Desktop-only (lg and up) — e.g. the Cmd+K coach mark. */
  desktopOnly?: boolean;
}

const MEMO_PAGE = /^\/app\/research\/(?!new(?:\/|$))[^/]+(\/(memo|discuss|quiz|opposing|drafts))?$/;
const CASE_PAGE = /^\/app\/cases\/[^/]+$/;

/** Every route the "Show me around" walkthrough touches (in chapter order).
 *  routeMatch covers ALL of them so the provider's route-change teardown
 *  never fires on the tour's own navigation. */
const AROUND_ROUTES = ['/app', '/app/research/new', '/app/research', '/app/drafts/new', '/app/cases'];

/** A tour with a routed step is driven by the cross-route engine. */
export function isCrossRoute(tour: TourDef): boolean {
  return tour.steps.some((s) => !!s.route);
}

export const TOURS: TourDef[] = [
  {
    id: 'around',
    kind: 'tour',
    label: 'Show me around',
    auto: false, // pull-only: never launches itself (spec §2.4)
    routeMatch: (p) => AROUND_ROUTES.includes(p),
    launchPath: '/app',
    // Anchor policy: every step must resolve on a BRAND-NEW account (empty
    // libraries anchor their headers; TodayPage's ghost sample carries
    // today-hearings). Multi-target steps pick the visible desktop/mobile twin.
    steps: [
      // ── Chapter 1 · Your day ────────────────────────────────────────────
      {
        key: 'around.1',
        route: '/app',
        chapter: 'day',
        targets: ['[data-tour="today-hearings"]'],
        side: 'bottom',
      },
      {
        key: 'around.2',
        route: '/app',
        chapter: 'day',
        targets: ['[data-tour="new-button"]', '[data-tour="new-button-mobile"]'],
        side: 'right',
        mobileSide: 'top',
      },
      // ── Chapter 2 · Research ────────────────────────────────────────────
      {
        key: 'around.3',
        route: '/app/research/new',
        chapter: 'research',
        targets: ['[data-tour="framing-composer"]'],
        side: 'top',
      },
      {
        key: 'around.4',
        route: '/app/research/new',
        chapter: 'research',
        targets: ['[data-tour="framing-attach"]'],
        side: 'top',
      },
      {
        // The run-as-written button only exists once text is typed — fall back
        // to the composer so the step still lands on an empty account.
        key: 'around.5',
        route: '/app/research/new',
        chapter: 'research',
        targets: ['[data-tour="run-as-written"]', '[data-tour="framing-composer"]'],
        side: 'top',
      },
      {
        key: 'around.6',
        route: '/app/research',
        chapter: 'research',
        targets: ['[data-tour="research-library"]'],
        side: 'bottom',
      },
      // ── Chapter 3 · Drafting ────────────────────────────────────────────
      {
        key: 'around.7',
        route: '/app/drafts/new',
        chapter: 'drafting',
        targets: ['[data-tour="draft-type-picker"]'],
        side: 'bottom',
      },
      {
        // The attach row lives on the interview view (after a type is picked)
        // — fall back to the picker so the step lands from the picker too.
        key: 'around.8',
        route: '/app/drafts/new',
        chapter: 'drafting',
        targets: ['[data-tour="draft-attach"]', '[data-tour="draft-type-picker"]'],
        side: 'bottom',
      },
      // ── Chapter 4 · Your cases ──────────────────────────────────────────
      {
        key: 'around.9',
        route: '/app/cases',
        chapter: 'cases',
        targets: ['[data-tour="cases-list"]'],
        side: 'bottom',
      },
      {
        key: 'around.10',
        route: '/app/cases',
        chapter: 'cases',
        targets: ['[data-tour="new-case"]'],
        side: 'bottom',
        mobileSide: 'bottom',
      },
      // ── Chapter 5 · Always available ────────────────────────────────────
      {
        key: 'around.11',
        route: '/app',
        chapter: 'always',
        targets: ['[data-tour="research-chat-button"]', '[data-tour="research-chat-fab"]'],
        side: 'bottom',
        mobileSide: 'top',
      },
      {
        key: 'around.12',
        route: '/app',
        chapter: 'always',
        targets: ['[data-tour="quota-meter"]'],
        side: 'bottom',
      },
      {
        key: 'around.13',
        route: '/app',
        chapter: 'always',
        targets: ['[data-tour="user-menu"]'],
        side: 'bottom',
      },
    ],
  },
  {
    id: 'memo',
    kind: 'tour',
    label: 'Your first memo',
    auto: true,
    routeMatch: (p) => MEMO_PAGE.test(p),
    launchPath: null, // needs a completed memo — Settings resolves one from jobsStore
    steps: [
      { key: 'memo.1', targets: ['[data-tour="citations-badge"]'], side: 'bottom' },
      { key: 'memo.2', targets: ['[data-tour="studio-tabs"]'], side: 'bottom' },
      { key: 'memo.3', targets: ['[data-tour="memo-drafts-tab"]'], side: 'bottom' },
    ],
  },
  {
    id: 'casefile',
    kind: 'tour',
    label: 'Your first case file',
    auto: true,
    routeMatch: (p) => CASE_PAGE.test(p),
    launchPath: null, // needs a case — Settings resolves one from mattersStore
    steps: [
      { key: 'casefile.1', targets: ['[data-tour="case-tabs"]'], side: 'bottom' },
      { key: 'casefile.2', targets: ['[data-tour="case-actions"]'], side: 'bottom' },
      {
        key: 'casefile.3',
        targets: ['[data-tour="log-outcome"]'],
        side: 'left',
        mobileSide: 'top',
      },
    ],
  },
  {
    id: 'drafting',
    kind: 'tour',
    label: 'Drafting',
    auto: true,
    routeMatch: (p) => p === '/app/drafts/new',
    launchPath: '/app/drafts/new',
    steps: [
      { key: 'drafting.1', targets: ['[data-tour="draft-type-picker"]'], side: 'bottom' },
      // NOTE: the attach callout lives on the interview view (after a type is
      // picked) and never coexists with the picker; steps are visible-filtered
      // at start, so this shows only when the tour is fired on that view.
      { key: 'drafting.2', targets: ['[data-tour="draft-attach"]'], side: 'top' },
    ],
  },
  {
    id: 'coach-assistant',
    kind: 'coach',
    label: 'The assistant',
    auto: true,
    routeMatch: (p) => p === '/app',
    launchPath: '/app',
    // "First Today visit after checklist starts" — approximated as second
    // session onward so it never competes with the brand-new-user checklist.
    minSessions: 2,
    steps: [
      { key: 'coach.assistant', targets: ['[data-tour="assistant-composer"]'], side: 'top' },
    ],
  },
  {
    id: 'coach-provenance',
    kind: 'coach',
    label: 'eCourts data',
    auto: true,
    routeMatch: (p) => p === '/app' || CASE_PAGE.test(p),
    launchPath: null,
    steps: [
      { key: 'coach.provenance', targets: ['[data-tour="provenance-chip"]'], side: 'bottom' },
    ],
  },
  {
    id: 'coach-cmdk',
    kind: 'coach',
    label: 'Quick jump',
    auto: true,
    routeMatch: (p) => p.startsWith('/app'),
    launchPath: null,
    minSessions: 5,
    desktopOnly: true,
    // Dormant by design: the global-search palette has not shipped yet, so no
    // element carries this anchor. The mark starts firing the moment a future
    // stage adds data-tour="global-search" to the palette trigger.
    steps: [{ key: 'coach.cmdk', targets: ['[data-tour="global-search"]'], side: 'bottom' }],
  },
];

export const AUTO_TOUR_PRIORITY: TourId[] = [
  'memo',
  'casefile',
  'drafting',
  'coach-assistant',
  'coach-provenance',
  'coach-cmdk',
];

export function getTour(id: TourId): TourDef {
  const t = TOURS.find((x) => x.id === id);
  if (!t) throw new Error(`Unknown tour: ${id}`);
  return t;
}

/** Tours listed on the Settings Help card (coach marks are not replayable). */
export const REPLAYABLE_TOURS: TourId[] = ['around', 'memo', 'casefile', 'drafting'];
